import { Stack } from "@mui/material";
import React, { useMemo } from "react";
import { useHistory, useParams } from "react-router";
import { useActionRequest } from "../../../../../../common/hooks/api";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { Api } from "../../../../../../common/service/Api";
import { CollectionContext } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UUIDString } from "../../../../../../common/service/server/Types";
import { PageHeader } from "../../../../../components/layout/header/Header";
import { StandardPageWrapper } from "../../../../../components/layout/StandardPageWrapper";
import {
  FcraCallout,
  GlbaCallout,
  HipaaCallout,
} from "../../../../../components/organisms/callouts/RegulationCallouts";
import { DataRecipientDisclosureCards } from "../../../../../components/organisms/DataRecipientCollectionCards";
import { usePrimaryOrganizationId } from "../../../../../hooks";
import {
  EXCLUDE_GROUP_EMPLOYMENT,
  EXCLUDE_GROUP_NON_EMPLOYMENT,
  useCollectionGroup,
} from "../../../../../hooks/useCollectionGroups";
import { useDisclosuresForCollectionGroups } from "../../../../../hooks/useDisclosuresForCollectionGroups";
import { useDataSourceAssociation } from "../../../../../hooks/useVendorAssociation";
import { RouteHelpers } from "../../../../../root/routes";
import { BUSINESS_SURVEY_NAME } from "../../../../../surveys/businessSurvey";
import { ProgressFooter, ProgressFooterActions } from "../../../../get-compliant/ProgressFooter";

type Params = {
  dataSubjectTypeId: UUIDString;
};

export const DisclosuresForGroupPage = () => {
  const history = useHistory();

  const { dataSubjectTypeId: collectionGroupId } = useParams<Params>();

  const orgId = usePrimaryOrganizationId();
  const { data: dataMap } = useDataMap(orgId);
  const { answers, request: surveyRequest } = useSurvey(orgId, BUSINESS_SURVEY_NAME);
  const [collectionGroup, collectionGroupsRequest] = useCollectionGroup(orgId, collectionGroupId);
  const groups = useMemo(() => [collectionGroup], [collectionGroup]);
  const isEmployment = collectionGroup?.collectionGroupType == "EMPLOYMENT";
  const context: CollectionContext[] = isEmployment ? ["EMPLOYEE"] : ["CONSUMER"];
  const disclosureData = useDisclosuresForCollectionGroups(orgId, [collectionGroup], context);

  const { fetched } = useDataSourceAssociation(
    orgId,
    collectionGroupId,
    isEmployment ? EXCLUDE_GROUP_NON_EMPLOYMENT : EXCLUDE_GROUP_EMPLOYMENT,
    isEmployment ? ["EMPLOYEE"] : ["CONSUMER"],
  );

  const hasSources = Boolean(fetched?.sources.length > 0);

  const { fetch: completeTasks } = useActionRequest({
    api: () => Api.tasks.completeTasks(orgId),
    onSuccess: () => {
      history.push(RouteHelpers.dataMap.collectionGroup.detail(collectionGroupId));
    },
  });

  const handleDone = () => completeTasks();

  return (
    <StandardPageWrapper
      requests={[
        collectionGroupsRequest,
        surveyRequest,
        disclosureData.requests.dataMapRequest,
        disclosureData.requests.recipientsRequest,
      ]}
      footer={
        <ProgressFooter
          actions={
            <ProgressFooterActions
              nextContent={hasSources ? "Next" : "Done"}
              nextDisabled={!disclosureData.complete}
              prevOnClick={() =>
                history.push(
                  RouteHelpers.dataMap.collectionGroup.workflow.dataRecipientAssociation(
                    collectionGroupId,
                  ),
                )
              }
              nextOnClick={() =>
                hasSources
                  ? history.push(
                      RouteHelpers.dataMap.collectionGroup.workflow.sourceAssociation(
                        collectionGroupId,
                      ),
                    )
                  : handleDone()
              }
            />
          }
        />
      }
    >
      <PageHeader titleContent={collectionGroup?.name} />

      <h4 className="intermediate">
        What categories of information are collected, analyzed by, stored in, or otherwise processed
        by your Data Recipients?
      </h4>

      <p>
        For each of your Data Recipients, identify which categories of personal data they process,
        including any categories of digital information that may be collected and processed
        automatically.
      </p>

      {answers["business-hipaa-compliant"] === "true" && (
        <div className="mb-md">
          <HipaaCallout />
        </div>
      )}
      {answers["business-glba-compliant"] === "true" && (
        <div className="mb-md">
          <GlbaCallout />
        </div>
      )}
      {answers["business-fcra-compliant"] === "true" && (
        <div className="mb-md">
          <FcraCallout />
        </div>
      )}

      <Stack spacing={3}>
        <DataRecipientDisclosureCards
          dataMap={dataMap}
          disclosureData={disclosureData}
          groups={groups}
          context={context}
        />
      </Stack>
    </StandardPageWrapper>
  );
};
